/**
 * useMultiSelect — Manages multi-node selection state.
 *
 * Works alongside the single-node `selectedNode` in Workbench.
 * When 2+ nodes are selected (via Ctrl+Click or box-select on the graph),
 * the Inspector switches to MultiSelectView.
 *
 * Hard limit: MAX_BATCH_SIZE = 50 for batch operations.
 */
import { useState, useCallback, useMemo } from 'react';
import type { GraphNodeData } from '../types/graph';

export const MAX_BATCH_SIZE = 50;

export interface MultiSelectState {
    /** Currently selected node IDs */
    selectedIds: Set<string>;
    /** Full node data for selected nodes (keyed by ID for fast lookup) */
    selectedNodes: Map<string, GraphNodeData>;
    /** Whether multi-select mode is active (2+ nodes) */
    isMultiSelect: boolean;
    /** Whether batch actions are over the limit */
    isBatchOverLimit: boolean;
    /** Count of selected nodes */
    count: number;
}

export interface MultiSelectActions {
    /** Add a node to the selection (Ctrl+Click) */
    addNode: (node: GraphNodeData) => void;
    /** Remove a node from the selection */
    removeNode: (nodeId: string) => void;
    /** Toggle a node in/out of selection */
    toggleNode: (node: GraphNodeData) => void;
    /** Set the entire selection at once (e.g., from box-select) */
    setSelection: (nodes: GraphNodeData[]) => void;
    /** Clear the multi-selection entirely */
    clearSelection: () => void;
    /** Get risk summary: { high, medium, low, unknown } */
    getRiskSummary: () => { high: number; medium: number; low: number; unknown: number };
    /** Get all selected nodes as an array */
    getSelectedArray: () => GraphNodeData[];
}

export function useMultiSelect(): MultiSelectState & MultiSelectActions {
    const [selectedNodes, setSelectedNodes] = useState<Map<string, GraphNodeData>>(new Map());

    const selectedIds = useMemo(() => new Set(selectedNodes.keys()), [selectedNodes]);
    const count = selectedNodes.size;
    const isMultiSelect = count >= 2;
    const isBatchOverLimit = count > MAX_BATCH_SIZE;

    const addNode = useCallback((node: GraphNodeData) => {
        setSelectedNodes(prev => {
            const next = new Map(prev);
            next.set(node.id, node);
            return next;
        });
    }, []);

    const removeNode = useCallback((nodeId: string) => {
        setSelectedNodes(prev => {
            const next = new Map(prev);
            next.delete(nodeId);
            return next;
        });
    }, []);

    const toggleNode = useCallback((node: GraphNodeData) => {
        setSelectedNodes(prev => {
            const next = new Map(prev);
            if (next.has(node.id)) {
                next.delete(node.id);
            } else {
                next.set(node.id, node);
            }
            return next;
        });
    }, []);

    const setSelection = useCallback((nodes: GraphNodeData[]) => {
        const map = new Map<string, GraphNodeData>();
        nodes.forEach(n => map.set(n.id, n));
        setSelectedNodes(map);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedNodes(new Map());
    }, []);

    const getRiskSummary = useCallback(() => {
        let high = 0, medium = 0, low = 0, unknown = 0;
        selectedNodes.forEach(node => {
            const score = node.risk_score;
            if (score == null) unknown++;
            else if (score >= 0.7) high++;
            else if (score >= 0.4) medium++;
            else low++;
        });
        return { high, medium, low, unknown };
    }, [selectedNodes]);

    const getSelectedArray = useCallback(() => {
        return Array.from(selectedNodes.values());
    }, [selectedNodes]);

    return {
        selectedIds,
        selectedNodes,
        isMultiSelect,
        isBatchOverLimit,
        count,
        addNode,
        removeNode,
        toggleNode,
        setSelection,
        clearSelection,
        getRiskSummary,
        getSelectedArray,
    };
}
